/* istanbul ignore file */ /* this code is not used or will be deleted in other branches */
import React, { useCallback, useState, useEffect } from 'react';
import useFieldApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-field-api';
import { isRequiredValidation } from '../../utils/is-required-validation';
import { useDdf } from '../../context/ddf-context';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { useModal } from '../Modal';

import { Trash, Info1 as ExclamationCircle, AlternativeAdd as Add } from '@td-client-frontend/icons';
import { FieldInputProps } from 'formik';

interface Props extends FieldInputProps<any> {
  label: string;
  error?: string;
  name: string;
  value: any;
  removeLogo: any;
  disabled?: boolean;
}

export interface FileWithPreview extends File {
  preview: string | null;
}

export function FileUpload({ label, onChange, value, removeLogo, ...props }: Props) {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const MIN_IMAGE_WIDTH = 300; // 300px width
  const MIN_IMAGE_HEIGHT = 525; // 525px height
  const { showModal } = useModal();

  useEffect(() => {
    const files = value && Array.isArray(value) ? value : [];
    setFiles(files);
  }, [value]);

  const imageMeetsRequiredDimensions = (file: HTMLImageElement) => {
    return file && file.width >= MIN_IMAGE_WIDTH && file.height >= MIN_IMAGE_HEIGHT;
  };

  const onDropAccepted = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles ? acceptedFiles[0] : null;
      const preview = URL.createObjectURL(file);
      const img = new Image();
      img.src = preview;

      const setLogo = () => {
        setFiles([
          Object.assign(file, {
            preview: preview
          })
        ]);

        onChange(acceptedFiles);
      };

      img.onload = () => {
        if (imageMeetsRequiredDimensions(img)) {
          setLogo();
        } else {
          showModal({
            size: 'prompt',
            header: 'Logo does not meet minimum requirements',
            content: (
              <p className="p-6">
                This logo does not meet the minimum requirements and we cannot guarantee quality of the printed
                materials.
              </p>
            ),
            actions: [
              {
                title: 'Cancel',
                kind: 'secondary'
              },
              {
                title: 'Continue',
                kind: 'primary',
                onClick: setLogo
              }
            ]
          });
        }
      };
    },
    [onChange, showModal]
  );

  const onDropRejected = useCallback((rejectedFiles) => {
    const file = rejectedFiles ? rejectedFiles.pop() : null;

    if (file && file.errors && file.errors[0]) {
      toast.error(file.errors[0].message, {
        position: 'top-center',
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDropAccepted,
    onDropRejected,
    maxSize: 510000,
    accept: 'image/jpeg',
    multiple: false,
    ...props
  });

  return (
    <div className="font-effra h-full">
      <div
        className="flex flex-col items-center justify-center w-64 h-64 text-base border-dashed rounded-lg cursor-pointer border-1 border-gray-default"
        {...getRootProps()}
      >
        <input {...getInputProps()} className="disabled:opacity-50 disabled:cursor-not-allowed" />

        <Add className="fill-current text-gray-default" title="Add File" aria-hidden />

        <p className="text-gray-default">Add File</p>
      </div>

      <button
        className="flex items-center justify-between text-base leading-tight focus:outline-none"
        onClick={open}
        title="Open file dialog"
        type="button"
      >
        <p className="text-sm text-gray-default">Drag & drop your file or click here</p>

        <ExclamationCircle className="fill-current text-primary-600" title="Open file dialog" aria-hidden />
      </button>
      <p className="text-sm text-gray-default">{label}</p>
      <div>
        <div className="pt-12">
          {files?.[0]?.preview && (
            <>
              <img alt="preview" src={files[0]?.preview} className="w-40 h-40 object-fill" />
              <button
                type="button"
                onClick={removeLogo}
                title="Remove Logo"
                disabled={props.disabled}
                className="flex items-center px-2 py-1 my-6 text-base leading-tight border rounded-lg border-primary-500"
              >
                <Trash
                  className="fill-current text-primary-500"
                  title="Remove Logo"
                  height={40}
                  width={40}
                  aria-hidden
                />
                <p className="pr-2 text-sm text-primary-500">Delete</p>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function FileUploadDdf(props: any) {
  const {
    input,
    meta: { error },
    label
  } = useFieldApi(props);
  const { groupType } = useDdf();
  const isRequired = isRequiredValidation(props.validate);

  const removeLogo = async () => {
    input.onChange('');
  };

  return (
    <FileUpload
      {...props}
      {...input}
      error={error}
      disabled={groupType === 'inactive'}
      required={isRequired}
      label={label}
      removeLogo={removeLogo}
    />
  );
}
